/* React Components */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
/* Components */
import { supabase } from '../../services/Supabase';
import commonFunction from "../../utils/CommonFunction";
import { useAuth } from '../../context/AuthContext';
/* React Icons */
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineExclamationCircle, AiOutlineClose } from "react-icons/ai";
import { FaSpinner } from 'react-icons/fa';
/* Styles and Images */
import CompanyLogoWhite from "../../assets/img/m-comp-logo-white.png";
import Loader from '../../components/Loader';

export default function SignIn() {
    /* Providers */
    const navigate = useNavigate();
    const { parseError } = commonFunction();
    const { user, userMetaData, loading } = useAuth();

    /* Use States / Setters */
    const [showPass, setShowPass] = useState(false);
    const [showErrorMsg, setShowErrorMsg] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [showConfirmPass, setShowConfirmPass] = useState(false);

    /* FORMS: Use States / Setters */
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        nickName: "",
        theme: "ari-theme",
        email: "",
        password: "",
        confirmPassword: ""
    });
    const { firstName, lastName, nickName, userTheme, email, password, confirmPassword } = formData;
    const companyLogo = CompanyLogoWhite;

    // Redirect if already logged in
    useEffect(() => {
        if (user && userMetaData) {
            const redirectURL = userMetaData.user_role === 'administrator' ? '/admin/dashboard' : '/dashboard';
            navigate(redirectURL);
        }
    }, [user, userMetaData, navigate]);

    /* Custom Functions */
    function onChange(e){
        setPasswordError('');
        setFormData((prevState) => ({
            ...prevState,
            [e.target.id]: e.target.value,
        }));
    }

    async function onSubmit(e) {
        e.preventDefault();

        setPasswordError('');

        if (formData.password !== formData.confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        setLoadingButton(true);

        try {
            const { data, error: signUpError } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/email-verified-callback`,
                    data: {
                        first_name: formData.firstName,
                        last_name: formData.lastName,
                    },
                },
            });

            if (signUpError) {
                const parsedError = parseError(signUpError);
                toast.error(parsedError.message);
                return;
            }

            const user = data?.user;
            const identities = user?.identities ?? [];

            // If no user came back, something is off
            if (!user) {
                toast.error("Registration failed. Please try again.");
                return;
            }

            // If identities is empty, Supabase often means the email already exists
            if (identities.length === 0) {
                toast.error("This email address is already registered. Please use a different email address and try again.");
                return;
            }

            const { error: insertError } = await supabase
                .from('users_metadata')
                .insert([
                    {
                        user_id: user.id,
                        user_email: user.email,
                        user_role: 'subscriber',
                        first_name: formData.firstName,
                        last_name: formData.lastName,
                        nickname: formData.nickName,
                        user_theme: formData.theme,
                    },
                ]);

            if (insertError) {
                const parsedError = parseError(insertError);
                toast.error(parsedError.message);
                return;
            }

            toast.success("Your registration was successful! Please check your inbox to verify your email address.");
            navigate("/");
        } catch (err) {
            const parsedError = parseError(err);
            toast.error(parsedError.message || "Something went wrong. Please try again.");
            console.error("Registration error:", err);
        } finally {
            setLoadingButton(false);
        }
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <section className="main-form-section signup-section">
            <div className="container">
                <div className="banner-wrapper">
                    <div className="form-section-wrapper">
                        <form onSubmit={onSubmit} method='POST'>
                            
                            <div className="form-header register-header"><img src={companyLogo} alt="Company Logo" /></div>
                            
                            {/* Show if error occured */}
                            {showErrorMsg ? (
                                <div className="alert alert-error">
                                    <div className="alert-content">
                                        <div className="icon__wrapper">
                                            <span><AiOutlineExclamationCircle /></span>
                                        </div>
                                        <p>An error occured. Whether a server error or email already in use.</p>
                                    </div>
                                    <span className="close" onClick={()=>setShowErrorMsg(false)}><AiOutlineClose /></span>
                                </div>
                            ) : ''}

                            <div className="login-heading">
                                <h2>Sign Up to ARI</h2>
                            </div>
                            {/* First Name */}
                            <div className="form-wrapper">
                                <label htmlFor="firstName">First Name *</label>
                                <div className="form-group">
                                    <input type="firstName" className="form-input" id="firstName" name="firstName" placeholder="First Name" value={firstName} onChange={onChange} required />
                                </div>
                            </div>
                            {/* Last Name */}
                            <div className="form-wrapper">
                                <label htmlFor="lastName">Last Name *</label>
                                <div className="form-group">
                                    <input type="lastName" className="form-input" id="lastName" name="lastName" placeholder="Last Name" value={lastName} onChange={onChange} required />
                                </div>
                            </div>
                            {/* Nick Name */}
                            <div className="form-wrapper">
                                <label htmlFor="nickName">Nickname *</label>
                                <div className="form-group">
                                    <input type="nickName" className="form-input" id="nickName" name="nickName" placeholder="Nickname" value={nickName} onChange={onChange} required />
                                </div>
                            </div>
                            {/* Theme */}
                            <div className="form-wrapper">
                                <label htmlFor="theme">Theme *</label>
                                <div className="form-group">
                                    <select name="theme" id="theme" className="form-input" onChange={onChange} value={userTheme}>
                                        <option value="ari-theme">Default Theme</option>
                                        <option value="dark-mode">Dark Mode</option>
                                        <option value="light-mode">Light Mode</option>
                                    </select>
                                </div>
                            </div>
                            {/* Email */}
                            <div className="form-wrapper">
                                <label htmlFor="email">Email Address *</label>
                                <div className="form-group">
                                    <input type="email" className="form-input" id="email" name="email" placeholder="Email Address" value={email} onChange={onChange} required />
                                </div>
                            </div>
                            {/* Password */}
                            <div className="form-wrapper">
                                <label htmlFor="password">Password *</label>
                                <div className="form-group">
                                    <input type={showPass ? "text" : "password"} className="form-input" id="password" name="password" placeholder="Password" value={password} onChange={onChange} required />
                                    <i className="show-icon">{showPass ? <AiOutlineEyeInvisible onClick={()=>setShowPass((prevState) => !prevState)} /> : <AiOutlineEye onClick={()=>setShowPass((prevState) => !prevState)} />}</i>
                                </div>
                            </div>
                            {/* Confirm Password */}
                            <div className="form-wrapper">
                                <label htmlFor="confirmPassword">Confirm Password *</label>
                                <div className="form-group">
                                    <input type={showConfirmPass ? "text" : "password"} className="form-input" id="confirmPassword" name="confirmPassword" placeholder="Confirm Password" value={confirmPassword} onChange={onChange} required />
                                    <i className="show-icon">{showConfirmPass ? <AiOutlineEyeInvisible onClick={()=>setShowConfirmPass((prevState) => !prevState)} /> : <AiOutlineEye onClick={()=>setShowConfirmPass((prevState) => !prevState)} />}</i>
                                </div>
                            </div>
                            {passwordError && <p style={{ color: 'red' }}>{passwordError}</p>}
                            <div className="form-group">
                                {loadingButton ? 
                                    (<button className="submit-button w-100" type="submit"><FaSpinner className="spinner" disabled /> Registering. Please wait... </button>) : 
                                    (<button className="submit-button w-100" id="submit-button" type="submit"> Register</button>)
                                }
                            </div>
                            <div className="sign-in-text">
                                <ul>
                                    <li>Already have an account? <Link to="/sign-in">Sign In</Link></li>
                                </ul>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    )
}
